import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { DisplayContactModel, getContactsStartsWith } from "client/Api/Email/contacts";
import {
	MultiComboBox,
	MultiComboBoxSuggestionsResult,
	SuggestedMultiComboBoxItem
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { getHighlight } from "client/Misc/highlight";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	delay,
	EMPTY_ARRAY,
	generateTrackingId,
	isNilOrWhiteSpace
} from "client/Misc/utils";
import { RecipientListItem } from "../Email/EmailContent/EmailComposer/Header/RecipientListItem";

const MAX_NUMBER_OF_ITEMS = 10;

export type AttendeesInputProps = {
	attachable: IValueInputAttachable<readonly SuggestedMultiComboBoxItem<DisplayContactModelWithoutId>[]>;
	onAfterAttendeeChange: (newValue: readonly SuggestedMultiComboBoxItem<DisplayContactModelWithoutId>[]) => void;
};

export const AttendeesInput = (props: AttendeesInputProps): ReactElement => {
	const {
		attachable,
		onAfterAttendeeChange
	} = props;

	const [suggesionsResult, setSuggesionsResult] = useState<MultiComboBoxSuggestionsResult<DisplayContactModelWithoutId>>({
		loadingStatus: "loading"
	});

	const findContactsHandler = useCallback(
		async (searchTerm: string, signal: AbortSignal): Promise<void> => {
			if (searchTerm.trim() === "") {
				setSuggesionsResult({
					loadingStatus: "ready",
					suggestions: EMPTY_ARRAY
				});
				return;
			}

			setSuggesionsResult({
				loadingStatus: "loading"
			});

			try {
				await delay(400, signal);

				const contacts: readonly DisplayContactModel[] = await getContactsStartsWith(
					searchTerm,
					MAX_NUMBER_OF_ITEMS,
					signal
				);

				setSuggesionsResult({
					loadingStatus: "ready",
					suggestions: contacts.map(
						(x): SuggestedMultiComboBoxItem<DisplayContactModelWithoutId> => ({
							text: x.displayName,
							value: x,
							highlight: getHighlight(x.displayName, searchTerm),
							valueTrackingId: `${x.displayName}-${x.emailAddress}`
						})
					)
				});
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				setSuggesionsResult({ loadingStatus: "error" });
				console.error(e);
			}
		},
		[]
	);

	const getCustomSuggestion = useCallback(
		(searchTerm: string): SuggestedMultiComboBoxItem<DisplayContactModelWithoutId> | null => {
			const shouldShowCustomSuggestion: boolean = (suggesionsResult.loadingStatus === "ready"
				&& suggesionsResult
					.suggestions
					.length === 0)
				|| suggesionsResult.loadingStatus === "error";

			if (isNilOrWhiteSpace(searchTerm) || !shouldShowCustomSuggestion) {
				return null;
			}

			return {
				text: searchTerm,
				highlight: getHighlight(searchTerm, searchTerm),
				value: {
					displayName: searchTerm,
					emailAddress: searchTerm
				},
				valueTrackingId: searchTerm
			};
		},
		[suggesionsResult]
	);

	return (
		<MultiComboBox
			{...attachable.attachToMultiComboBox().withOnChange(onAfterAttendeeChange)}
			onFind={findContactsHandler}
			placeholder={localize("_InviteAttendees")}
			renderSuggestionBox={
				(props, ref) => (
					<MultiComboBox.SuggestionBox
						baseProps={props}
						ref={ref}
						suggestionsResult={suggesionsResult}
						menuTitle={localize("_Attendees")}
						customSuggestion={getCustomSuggestion}
						emptySearchTermBehavior="show-hint"
					>
						{(item, onClick) => (
							<RecipientListItem
								trackingId={generateTrackingId("contact", `|${item.value.emailAddress}`)}
								item={item.value}
								highlights={{ displayName: item.highlight }}
								onClick={onClick}
							/>
						)}
					</MultiComboBox.SuggestionBox>
				)
			}
		/>
	);
};
AttendeesInput.displayName="AttendeesInput";

export type DisplayContactModelWithoutId = Omit<DisplayContactModel, "id">;